import { useEffect, useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAtom, useSetAtom } from 'jotai'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

import type { ProductDetail } from 'types/receipt/model'
import { Analytics } from 'analytics/analytics.events'
import {
  getProductInfo,
  updateMissings,
  validateContainer,
  validateProductRequest
} from 'client/receipt/receipt.client'
import {
  PRODUCT_STATUS,
  RECEIPTS,
  MISSING_TYPES,
  ERRORS,
  DEFAULT_FIELDS_EXTERNAL_PRODUCT
} from 'utils/constants'
import { extractWeight, normalizeEanForCapture } from 'utils/helpers'
import { toastError } from 'utils/toast'
import { useQueryParam, useRequiredParams } from 'hooks/params.hook'
import {
  containerTypeAtom,
  currentOrderAtom,
  scannedContainer,
  selectedProductAtom
} from '../Receipt.state'
import { isAnOrder } from './OrderDetail.loader'
import { useConfirmationDialog } from 'components/molecules/ConfirmationDialog.molecule'
import { useGoToScanContainer } from '../scan-container/ScanContainer.navigator'
import { useGoToMoveContainers } from '../move-containers/MoveContainers.navigator'
import { useGoToSummary } from '../summary/Summary.navigator'
import { useGoToReceipt } from '../Receipt.navigator'
import { useGoToRejection } from '../rejection/Rejection.navigator'
import { useGoToProductLots } from '../product-lots/ProductLots.navigator'
import { defaultActiveTab, groupByStatus, tabs } from './OrderDetail.helpers'
import { getCurrentCountry, getCurrentWarehouse } from 'client/utilities.client-helper'
import warehouses from '@justomx/warehouses'

export const useOrderDetailData = () => {
  const data = useLoaderData() as { products: ProductDetail[] }
  const { orderId, receiptId, receiptType } = useRequiredParams(
    'orderId',
    'receiptId',
    'receiptType'
  )
  const mode = useQueryParam('mode')
  const { t } = useTranslation('global')
  const navigate = useNavigate()

  const [tab, setTab] = useState(defaultActiveTab)
  const [loading, setLoading] = useState(false)
  const [scannedBarcode, setScannedBarcode] = useState<string>('')
  const [isFullMissing, setIsFullMissing] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  const [order, setOrder] = useAtom(currentOrderAtom)
  const [container, setContainer] = useAtom(scannedContainer)
  const setSelectedProduct = useSetAtom(selectedProductAtom)
  const setContainerType = useSetAtom(containerTypeAtom)

  const scanContainer = useGoToScanContainer()
  const moveContainers = useGoToMoveContainers()
  const summary = useGoToSummary()
  const receipt = useGoToReceipt()
  const rejection = useGoToRejection()
  const productLots = useGoToProductLots()
  const {
    dialogRef: missingsDialogRef,
    open: openMissingDialog,
    value: missingsDialogRefValue
  } = useConfirmationDialog()
  const {
    dialogRef: fullMissingDialogRef,
    open: openFullMissingDialog,
    value: missingDialogRefValue
  } = useConfirmationDialog()
  const { dialogRef: newProductDialog, open: openNewProductDialog } =
    useConfirmationDialog()
  const { dialogRef: upcDialog, open: openUpcDialog } = useConfirmationDialog()
  const newTransfersFlow = useFeatureIsOn('receipt_web_transfers_v2')
  const products = groupByStatus(data.products)

  useEffect(() => {
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    if (isInitialized || order) {
      if (orderId !== order) {
        setContainer(undefined)
        console.info('setting container to undefined')
      } else {
        console.info('same order, keeping last container in state', container)
        if (container) {
          validateContainer(container, receiptId)
            .then((validation) => {
              if (
                validation.containerExists &&
                !validation.locationPreStorage
              ) {
                setContainer(undefined)
                return
              }

              if (validation.containerUsedPreviously) {
                setContainer(undefined)
                return
              }

              console.info('container validation', validation)
              const country = getCurrentCountry()
              const validEmptyContainer =
                !validation.containerHasItems || country === 'PE'
              if (!validEmptyContainer) {
                setContainer(undefined)
              }
            })
            .catch((error) => {
              throw new Error(`${error}`)
            })
        }
      }
      setOrder(orderId)
    }
  }, [order, orderId, isInitialized])

  if (!isAnOrder(data)) {
    toastError(t('receipt-order-detail.type-error'))
    throw new Error(t('receipt-order-detail.type-error'))
  }

  const onSelectTab = (selectedTab: string) => {
    setTab(selectedTab)
  }

  const getTabs = () => {
    return tabs.map((id) => ({
      id,
      label: t(`receipt-order-detail.${id}`, {
        counter: products[id]?.length || 0
      })
    }))
  }

  const getBarcodeToSearch = (barcode: string): string => {
    let barcodeToSearch = barcode
    // TEMP: weekend temp fix
    const omitValidationsBarcodes = [
      '2838333615398'
    ]
    if (omitValidationsBarcodes.includes(barcodeToSearch)) return barcodeToSearch
    // TEMP: weekend temp fix
    const grams = extractWeight(barcode)
    if (grams !== null) {
      barcodeToSearch = normalizeEanForCapture(barcode)
    }
    return barcodeToSearch
  }

  const findProductInOrder = (barcode: string): ProductDetail => {
    const product = data.products.find((prod) => prod.barcode === barcode)
    if (!product) {
      throw new Error(ERRORS.upcNotFound)
    } else if (product.status !== PRODUCT_STATUS.pending) {
      throw new Error(ERRORS.alreadyReceived)
    }
    return product
  }

  const confirmValidScannedProduct = (product: ProductDetail) => {
    setContainerType('')
    setSelectedProduct(product)
    if (container && product) {
      productLots.go(receiptId, orderId, product.productId, false)
    } else {
      scanContainer.go(receiptId, orderId)
    }
  }

  const tryAddProductToOrder = async () => {
    try {
      setLoading(true)
      const { unitsOfMeasurement: unitsOfMeasurment, ...rest } =
        await getProductInfo(scannedBarcode)
      await validateProduct(scannedBarcode)
      const productDetail: ProductDetail = {
        unitsOfMeasurment,
        ...rest,
        ...DEFAULT_FIELDS_EXTERNAL_PRODUCT
      }
      confirmValidScannedProduct(productDetail)
    } catch (error) {
      if (
        error.message &&
        Object.values(ERRORS).includes(error.message as string)
      ) {
        toastError(t(`receipt-order-detail.${error.message}`, { upc: scannedBarcode }))
      } else {
        let errorKey = 'validations.get-product-error'
        if (error.response?.data?.message?.includes('not found')) {
          errorKey = 'validations.product-not-found'
        }
        toastError(t(errorKey))
      }
    } finally {
      setLoading(false)
    }
  }

  const validateProduct = async (barcode: string) => {
    const validation = await validateProductRequest(barcode)
    const currentWarehouseCode = getCurrentWarehouse()
    const currentWarehouse = await warehouses.get(currentWarehouseCode.toUpperCase())
    if (!validation.active) {
      throw new Error(ERRORS.inactiveProduct)
    }
    if (!validation.hasLocation && currentWarehouse?.type !== 'cedis') {
      throw new Error(ERRORS.noLocation)
    }
  }

  const scanProduct = async (upc: string) => {
    const barcodeToSearch = getBarcodeToSearch(upc)
    setScannedBarcode(barcodeToSearch)
    try {
      const scannedProduct = findProductInOrder(barcodeToSearch)
      await validateProduct(barcodeToSearch)
      confirmValidScannedProduct(scannedProduct)
    } catch (error) {
      if (
        error.message === ERRORS.upcNotFound &&
        newTransfersFlow &&
        receiptType === RECEIPTS.transfers
      ) {
        openNewProductDialog()
        return
      }
      if (
        error.message &&
        Object.values(ERRORS).includes(error.message as string)
      ) {
        toastError(t(`receipt-order-detail.${error.message}`, { upc }))
      }
    }
  }

  const onSelectProduct = (product: ProductDetail) => {
    setIsFullMissing(!!product.isFullMissing)
    if (product.status === PRODUCT_STATUS.pending) {
      if (receiptType === RECEIPTS.transfers) {
        openFullMissingDialog(product)
      } else {
        openMissingDialog(product)
      }
    } else {
      if (product.isFullMissing) {
        openFullMissingDialog(product)
      } else {
        setSelectedProduct(product)
        summary.go(receiptId, orderId, product.productId)
      }
    }
  }

  const onSelectMissingType = async (type: string) => {
    const selectedProduct = missingsDialogRefValue as ProductDetail
    if (type === MISSING_TYPES.fullMissing) {
      await onConfirmFullMissing(selectedProduct)
    } else {
      setSelectedProduct(selectedProduct)
      rejection.go(receiptId, orderId, selectedProduct.productId)
    }
  }

  const goToContainersPage = () => {
    moveContainers.go(receiptId, orderId)
  }

  const onConfirmFullMissing = async (product: ProductDetail) => {
    try {
      setLoading(true)
      const {
        productId: id,
        barcode,
        totalUnits: expectedQuantity,
        isFullMissing = false
      } = product
      const newIsFullMissing = !isFullMissing
      await updateMissings({
        product: { id, barcode, expectedQuantity },
        receiptId,
        isFullMissing: newIsFullMissing
      })
      Analytics.receipt.setFullMissingStatus({ orderId, receiptType }, product)

      if (newIsFullMissing && products.pending?.length === 1) {
        goToContainersPage()
      } else {
        navigate(0)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onSelectKeyboard = () => {
    openUpcDialog()
  }

  const onConfirmManualUpc = async (upc: string) => {
    if (!upc?.length) return
    Analytics.receipt.enterManualUpc({
      orderId,
      receiptType,
      ean: upc
    })
    await scanProduct(upc.trim())
  }

  return {
    orderDetail: data,
    orderId,
    products,
    tab,
    missingsDialogRef,
    fullMissingDialogRef,
    loading,
    isFullMissing,
    missingDialogRefValue,
    mode,
    upcDialog,
    receiptType,
    newTransfersFlow,
    newProductDialog,
    actions: {
      scanProduct,
      onSelectTab,
      getTabs,
      onSelectProduct,
      onConfirmFullMissing,
      onSelectMissingType,
      goToContainersPage,
      onSelectKeyboard,
      onConfirmManualUpc,
      tryAddProductToOrder,
      goToReceipt: () => {
        receipt.go()
      }
    }
  }
}
